<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import type { DashboardConsole } from '@/api/modules/dashboard'
import { fetchDashboardConsole } from '@/api/modules/dashboard'
import { useUserStore } from '@/stores/user'

const userStore = useUserStore()
const data = ref<DashboardConsole | null>(null)
const loading = ref(false)

const KPI_ROWS = [
  { todayLabel: '今日注册用户数', totalLabel: '用户总数', dayKey: 'd2', totalKey: 'd1', kind: 'int' as const },
  { todayLabel: '今日借款订单笔数', totalLabel: '借款订单总笔数', dayKey: 'd6', totalKey: 'd5', kind: 'int' as const },
  { todayLabel: '今日借款金额合计', totalLabel: '累计借款金额', dayKey: 'd4', totalKey: 'd3', kind: 'money' as const },
  {
    todayLabel: '今日支付单笔数（payorder）',
    totalLabel: '支付单累计笔数',
    dayKey: 'd10',
    totalKey: 'd9',
    kind: 'int' as const,
  },
  {
    todayLabel: '今日支付单金额合计',
    totalLabel: '支付单累计金额',
    dayKey: 'd8',
    totalKey: 'd7',
    kind: 'money' as const,
  },
]

function fmtInt(v: unknown) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('zh-CN', { maximumFractionDigits: 0 })
}

function fmtMoney(v: unknown) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

function fmtCell(kind: 'int' | 'money', v: unknown) {
  return kind === 'money' ? fmtMoney(v) : fmtInt(v)
}

const kpiTableData = computed(() => {
  const k = data.value?.kpis
  if (!k)
    return KPI_ROWS.map((r) => ({
      todayLabel: r.todayLabel,
      todayVal: '—',
      totalLabel: r.totalLabel,
      totalVal: '—',
    }))
  return KPI_ROWS.map((r) => ({
    todayLabel: r.todayLabel,
    todayVal: fmtCell(r.kind, k[r.dayKey]),
    totalLabel: r.totalLabel,
    totalVal: fmtCell(r.kind, k[r.totalKey]),
  }))
})

async function refresh() {
  loading.value = true
  try {
    const { data: body } = await fetchDashboardConsole()
    data.value = body
  } finally {
    loading.value = false
  }
}

onMounted(refresh)
</script>

<template>
  <PageChrome title="工作台 · 业务概览" description="统计数据「今日」按运营时区计算；口径与历史 PHP 管理中心 Main/index（d1–d10）一致。">
    <template #actions>
      <el-button :loading="loading" type="primary" @click="refresh">
        <template v-if="!loading">刷新数据</template>
        <template v-else>加载中…</template>
      </el-button>
    </template>

    <el-alert type="info" show-icon class="dash-inline-alert" :closable="false">
      当前登录 <strong>{{ userStore.username }}</strong>
      · 时区 <code>{{ data?.kpiTimeZone ?? '—' }}</code>
    </el-alert>

    <el-card class="dash-card" shadow="hover">
      <template #header>
        <span class="dash-card-head">核心业务指标</span>
      </template>
      <el-table
        :data="kpiTableData"
        stripe
        border
        size="default"
        class="ep-table-scope dash-metrics"
        :empty-text="loading ? '加载中…' : '暂无数据'"
      >
        <el-table-column prop="todayLabel" label="" min-width="200" align="left" />
        <el-table-column prop="todayVal" label="" width="140" align="right" />
        <el-table-column prop="totalLabel" label="" min-width="220" align="left" />
        <el-table-column prop="totalVal" label="" width="168" align="right" />
      </el-table>
    </el-card>

    <el-row :gutter="16" class="dash-split">
      <el-col :xs="24" :md="17">
        <el-card shadow="hover" class="dash-card">
          <template #header><span class="dash-card-head">运行环境概要</span></template>
          <el-descriptions
            v-if="data?.basicSystemInformation"
            direction="horizontal"
            :column="2"
            border
            size="default"
            class="dash-desc"
          >
            <el-descriptions-item
              v-for="(text, label) in data.basicSystemInformation"
              :key="label"
              :label="label"
              label-class-name="dash-desc-label"
            >
              <span class="dash-mono">{{ text }}</span>
            </el-descriptions-item>
          </el-descriptions>
        </el-card>
      </el-col>
      <el-col :xs="24" :md="7">
        <el-card shadow="hover" class="dash-card">
          <template #header><span class="dash-card-head">本账号最近登录</span></template>
          <el-table :data="data?.recentAdminLoginRows ?? []" stripe border size="small" class="ep-table-scope">
            <el-table-column prop="ipAddress" label="登录 IP" min-width="116" />
            <el-table-column prop="loginTime" label="登录时间" min-width="158" />
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <el-card class="dash-card dash-card-last" shadow="hover">
      <template #header><span class="dash-card-head">服务与宿主环境</span></template>
      <el-descriptions
        v-if="data?.serverInformation"
        direction="vertical"
        :column="1"
        border
        size="default"
        class="dash-desc"
      >
        <el-descriptions-item
          v-for="(text, label) in data.serverInformation"
          :key="label"
          :label="label"
          label-class-name="dash-desc-label dash-desc-label-wide"
        >
          <span class="dash-mono">{{ text }}</span>
        </el-descriptions-item>
      </el-descriptions>
    </el-card>
  </PageChrome>
</template>

<style scoped lang="scss">
.dash-inline-alert {
  margin-bottom: 18px;
  border-radius: var(--ep-radius-md);
}

.dash-card {
  margin-bottom: 18px;
  border-radius: var(--ep-radius-lg) !important;
}

.dash-card-last {
  margin-bottom: 0;
}

.dash-card-head {
  font-size: 15px;
  font-weight: 600;
  color: var(--ep-text-strong);
}

.dash-metrics {
  &:deep(.el-table__header-wrapper) {
    display: none;
  }
  &:deep(.el-table__cell) {
    padding: 12px 14px;
    font-size: 14px;
  }
}

.dash-split {
  margin-bottom: 0;
}

.dash-desc {
  &:deep(.dash-desc-label) {
    width: 180px;
    font-weight: 600;
    color: #475569;
    background: #f8fafc;
  }
  &:deep(.dash-desc-label-wide) {
    width: 220px;
  }
}

.dash-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  color: #0f172a;
  word-break: break-word;
}
</style>
